import { FilePurpose } from '@/api/enums';
import { isKeycloakEnabled } from '@/auth';
import { client } from '@/client';
import {
  InteractiveElement,
  InteractiveElementType,
  SlideTemplateType,
} from '@/client/courses/types/courses.type';
import { File } from '@/client/files';
import {
  COURSE_THUMBNAIL_MAX_SIZE,
  SLIDE_IMAGE_ACCEPTED_FORMATS,
} from '@/common/constants/files';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import {
  SlideImageUploadDefaultIcon,
  SlideImageUploadEmailIcon,
  SlideImageUploadSMSIcon,
} from '@/utils/helpers/icons.helper';
import { FileUpload, FileUploadUploadEvent } from 'primereact/fileupload';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

const StyledFlexContainer = styled(FlexContainer)`
  position: relative;
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  margin-top: 6px;

  &.white {
    background: var(--white-main);
  }
`;

const StyledUploadWrapper = styled(FlexContainer)`
  flex: 1 1 45%;
  background: var(--beige-main);
  border-radius: 4px;
  min-width: 200px;
  min-height: 90px;
  padding: var(--small-padding);
  word-break: break-word;
  position: relative;

  .icon {
    color: var(--gray-darker);
    font-size: var(--body-font-size);
  }

  .p-progress-spinner {
    width: 24px;
    height: 24px;
  }
`;

const StyledThumbnail = styled(FlexContainer)`
  overflow: hidden;
  border-radius: 4px;

  img {
    max-width: 100px;
    max-height: 100px;
    width: auto;
    height: auto;
  }
`;

const StyledInteractiveElementIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: var(--beige-light);
  margin: auto;

  i {
    margin: auto;
    font-size: 2rem;
    color: var(--gray-darker);
  }
`;

const StyledError = styled.p`
  color: var(--red-main);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  width: 100%;
`;

const StyledLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  font-weight: 500;
`;

const HiddenFileUpload = styled.div`
  display: none;
`;

const StyledRemoveButton = styled(AppButton)`
  position: absolute;
  top: 0;
  right: 0;
`;

const StyledDropdownButton = styled(AppButton)`
  width: 180px !important;
`;

type SlideImageUploadProps = {
  label?: string;
  tooltip?: string;
  icon?: React.ReactNode;
  preselectedFile?: File | null;
  preselectedInteractiveElement?: InteractiveElement | null;
  onFileUpload: (file: File | null) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
  isDisabled?: boolean;
  required?: boolean;
  hasError?: boolean;
  additionalImgOptions?: React.ReactNode;
  slideId?: string;
  templateType?: SlideTemplateType;
  purpose?: FilePurpose;
  onInteractiveElementAdd?: (
    slideId: string,
    interactiveElement: InteractiveElement | undefined,
  ) => void;
  onInteractiveElementUpdate?: (
    interactiveElement: InteractiveElement | undefined,
  ) => void;
};

export const SlideImageUpload: React.FC<SlideImageUploadProps> = ({
  label,
  tooltip,
  icon,
  preselectedFile,
  preselectedInteractiveElement,
  onFileUpload,
  setIsSubmitDisabled,
  isDisabled,
  hasError,
  additionalImgOptions,
  slideId,
  templateType,
  purpose,
  onInteractiveElementAdd,
  onInteractiveElementUpdate,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isKeycloakAuth = isKeycloakEnabled();
  const [error, setError] = useState<string[]>([]);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedInteractiveElement, setSelectedInteractiveElement] =
    useState<InteractiveElement | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const fileUploadRef = useRef<FileUpload>(null);
  const menuRef = useRef<Menu>(null);

  const validTypes = SLIDE_IMAGE_ACCEPTED_FORMATS.map(
    (format) => `image/${format.slice(1)}`,
  );

  useEffect(() => {
    setSelectedFile(preselectedFile || null);
  }, [preselectedFile]);

  useEffect(() => {
    setSelectedInteractiveElement(preselectedInteractiveElement || null);
  }, [preselectedInteractiveElement]);

  const onUpload = async (e: FileUploadUploadEvent) => {
    const response = JSON.parse(e.xhr.response);

    setSelectedFile(response);

    setError([]);

    onFileUpload(response);
    changeUploadingState(false);
  };

  const clearUpload = () => {
    setSelectedFile(null);
    onFileUpload(null);
    setError([]);
  };

  const clearInteractiveElement = () => {
    setSelectedInteractiveElement(null);
    if (slideId && onInteractiveElementAdd) {
      onInteractiveElementAdd(slideId, undefined);
    } else if (onInteractiveElementUpdate) {
      onInteractiveElementUpdate(undefined);
    }
  };

  const changeUploadingState = (isUploading: boolean) => {
    setIsUploading(isUploading);
    setIsSubmitDisabled(isUploading);
  };

  const handleUploadImage = () => {
    fileUploadRef.current?.getInput()?.click();
  };

  const handleAddSMS = () => {
    setSelectedFile(null);
    onFileUpload(null);
    const newElement = {
      type: InteractiveElementType.SMS,
      data: {},
    };
    setSelectedInteractiveElement(newElement);
    if (slideId && onInteractiveElementAdd) {
      onInteractiveElementAdd(slideId, newElement);
    } else if (onInteractiveElementUpdate) {
      onInteractiveElementUpdate(newElement);
    }
  };

  const handleAddEmail = () => {
    setSelectedFile(null);
    onFileUpload(null);
    const newElement = {
      type: InteractiveElementType.EMAIL,
      data: {},
    };
    setSelectedInteractiveElement(newElement);
    if (slideId && onInteractiveElementAdd) {
      onInteractiveElementAdd(slideId, newElement);
    } else if (onInteractiveElementUpdate) {
      onInteractiveElementUpdate(newElement);
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: t('generic.upload.image'),
      command: handleUploadImage,
    },
    // Only show SMS and Email options if not gallery type
    ...(templateType !== SlideTemplateType.GALLERY
      ? [
          {
            label: t('content.sms.add'),
            command: handleAddSMS,
          },
          {
            label: t('content.email.add'),
            command: handleAddEmail,
          },
        ]
      : []),
  ];

  const toggleMenu = (event: SyntheticEvent<HTMLButtonElement>) => {
    menuRef.current?.toggle(event);
  };

  const getButtonLabel = () => {
    if (selectedInteractiveElement) {
      return selectedInteractiveElement.type === InteractiveElementType.EMAIL
        ? t('content.email.add')
        : t('content.sms.add');
    }
    if (selectedFile?.id) {
      return t('generic.upload.image');
    }
    return t('generic.upload.image');
  };

  return (
    <div>
      <StyledLabel className={hasError ? 'p-error' : undefined}>
        {label && (
          <>
            {label} {tooltip ? <span className="red ml-1"> *</span> : ''}
          </>
        )}
        {tooltip && (
          <>
            <i className="pi pi-info-circle ml-2" />
            <Tooltip
              target=".pi-info-circle"
              content={tooltip}
              my="center bottom-20"
            />
          </>
        )}
      </StyledLabel>
      <StyledFlexContainer
        gap={24}
        justify="space-between"
        wrap="wrap"
        className="white"
      >
        <StyledUploadWrapper className="flex-1">
          {selectedFile?.id && !isUploading && (
            <FlexContainer justify="space-between">
              <StyledThumbnail flex="1">
                <img
                  src={selectedFile?.signedUrl}
                  alt={selectedFile?.fileName}
                />
              </StyledThumbnail>
              {!isDisabled && (
                <StyledRemoveButton
                  type="text"
                  severity="secondary"
                  icon="pi pi-times"
                  onClick={clearUpload}
                />
              )}
            </FlexContainer>
          )}
          {selectedInteractiveElement && !selectedFile?.id && !isUploading && (
            <FlexContainer justify="space-between">
              <StyledInteractiveElementIcon>
                {selectedInteractiveElement.type === 'email' ? (
                  <SlideImageUploadEmailIcon />
                ) : (
                  <SlideImageUploadSMSIcon />
                )}
              </StyledInteractiveElementIcon>
              {!isDisabled && (
                <StyledRemoveButton
                  type="text"
                  severity="secondary"
                  icon="pi pi-times"
                  onClick={clearInteractiveElement}
                />
              )}
            </FlexContainer>
          )}
          {!selectedFile?.id &&
            !selectedInteractiveElement &&
            !isUploading &&
            (icon ? icon : <SlideImageUploadDefaultIcon />)}
          {isUploading && <ProgressSpinner />}
        </StyledUploadWrapper>
        {!isDisabled && (
          <FlexContainer
            direction="column"
            align="flex-start"
            gap={12}
            flex="0 0 max-content"
          >
            <Menu
              model={menuItems}
              popup
              ref={menuRef}
              id="slide-upload-menu"
              style={{ width: 'auto', minWidth: '196px' }}
            />
            <StyledDropdownButton
              type="outlined"
              label={getButtonLabel()}
              icon="pi pi-angle-down"
              iconPos="right"
              aria-controls="slide-upload-menu"
              onClick={toggleMenu}
            />
            <HiddenFileUpload>
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                name="file"
                url={client.files.getFileUploadUrl()}
                accept={SLIDE_IMAGE_ACCEPTED_FORMATS.join(',')}
                maxFileSize={COURSE_THUMBNAIL_MAX_SIZE}
                onUpload={onUpload}
                auto
                withCredentials
                onValidationFail={(file) => {
                  if (file.size >= COURSE_THUMBNAIL_MAX_SIZE) {
                    setError((state) => [
                      t('error.fileSize', {
                        mb: COURSE_THUMBNAIL_MAX_SIZE / (1000 * 1000),
                      }),
                      ...state,
                    ]);
                  }
                }}
                onSelect={(event) => {
                  const file = event.files[0];

                  if (validTypes?.length && !validTypes.includes(file.type)) {
                    setError((state) => [
                      t('error.invalidFileType', {
                        types: validTypes.join(', '),
                      }),
                      ...state,
                    ]);
                  }
                }}
                onError={(event) => {
                  const response = JSON.parse(event?.xhr?.response);
                  const ignoreBackendValidationError =
                    error.includes('File must be of type') &&
                    validTypes?.length;

                  if (response?.message && ignoreBackendValidationError) {
                    setError((state) => [response.message, ...state]);
                  }
                  changeUploadingState(false);
                }}
                onBeforeSend={(event) => {
                  if (user && isKeycloakAuth) {
                    event.xhr.setRequestHeader(
                      'Authorization',
                      `Bearer ${user?.access_token}`,
                    );
                  }
                }}
                onBeforeUpload={(event) => {
                  clearUpload();
                  changeUploadingState(true);
                  event.formData.append(
                    'purpose',
                    purpose || FilePurpose.MATERIAL,
                  );
                }}
              />
            </HiddenFileUpload>
            {additionalImgOptions}
          </FlexContainer>
        )}
        {!!error?.length && (
          <StyledError>
            {error.map((error, index) => (
              <span key={index}>
                {error}.<br />
              </span>
            ))}
          </StyledError>
        )}
      </StyledFlexContainer>
    </div>
  );
};
